import React from 'react';
import { DropZone } from '@measured/puck';
import { resolveBackgroundColor, backgroundColorOptions } from './theme/colors';

const ColumnsComponent = ({
  columns = 2,
  gap = 'lg',
  stackOnMobile = true,
  verticalAlign = 'start',
  backgroundColor = 'transparent',
  padding = 'xl',
  maxWidth = '7xl',
  columnBackground = 'transparent',
  columnPadding = 'md',
  columnBorderRadius = 'lg',
  showDividers = false,
  dividerColor = 'gray-200'
}) => {
  const columnCount = Math.min(Math.max(columns, 1), 4);

  const containerBackground = resolveBackgroundColor(backgroundColor, 'transparent');
  const columnBackgroundConfig = resolveBackgroundColor(columnBackground, 'transparent');

  const getPaddingClass = () => {
    const mapping = {
      none: 'py-0 px-0',
      sm: 'py-8 px-4',
      md: 'py-12 px-4 md:px-6',
      lg: 'py-16 px-6 md:px-8',
      xl: 'py-20 px-6 md:px-10'
    };
    return mapping[padding] || 'py-20 px-6 md:px-10';
  };

  const getMaxWidthClass = () => {
    const mapping = {
      '3xl': 'max-w-3xl',
      '4xl': 'max-w-4xl',
      '5xl': 'max-w-5xl',
      '6xl': 'max-w-6xl',
      '7xl': 'max-w-7xl',
      full: 'max-w-full'
    };
    return mapping[maxWidth] || 'max-w-7xl';
  };

  const getGridColsClass = () => {
    if (columnCount === 1) return 'grid-cols-1';
    if (columnCount === 2) return stackOnMobile ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-2';
    if (columnCount === 3) return stackOnMobile ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-3';
    return stackOnMobile ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' : 'grid-cols-4';
  };

  const getGapClass = () => {
    const mapping = {
      xs: 'gap-4',
      sm: 'gap-6',
      md: 'gap-8',
      lg: 'gap-10',
      xl: 'gap-12'
    };
    return mapping[gap] || 'gap-10';
  };

  const getAlignClass = () => {
    const mapping = {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch'
    };
    return mapping[verticalAlign] || 'items-start';
  };

  const getColumnPaddingClass = () => {
    const mapping = {
      none: 'p-0',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8'
    };
    return mapping[columnPadding] || 'p-6';
  };

  const getColumnRadiusClass = () => {
    const mapping = {
      none: 'rounded-none',
      sm: 'rounded-md',
      md: 'rounded-lg',
      lg: 'rounded-xl',
      xl: 'rounded-2xl'
    };
    return mapping[columnBorderRadius] || 'rounded-xl';
  };

  const getDividerClass = () => {
    if (!showDividers) return '';
    const mapping = {
      'gray-200': 'border border-gray-200 dark:border-slate-700',
      'gray-300': 'border border-gray-300 dark:border-slate-600',
      'blue-200': 'border border-blue-200 dark:border-blue-800',
      'slate-700': 'border border-slate-700'
    };
    return mapping[dividerColor] || 'border border-gray-200';
  };

  const dropZoneGap = {
    xs: '0.75rem',
    sm: '1rem',
    md: '1.25rem',
    lg: '1.5rem',
    xl: '2rem'
  }[gap] || '1.5rem';

  const dropZoneStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: dropZoneGap,
    height: '100%',
    width: '100%'
  };

  return (
    <section className={`w-full ${containerBackground.className}`} style={containerBackground.style}>
      <div className={`${getMaxWidthClass()} mx-auto ${getPaddingClass()}`}>
        <div className={`grid ${getGridColsClass()} ${getGapClass()} ${getAlignClass()}`}>
          {Array.from({ length: columnCount }).map((_, index) => (
            <div
              key={index}
              className={`${columnBackgroundConfig.className} ${getColumnPaddingClass()} ${getColumnRadiusClass()} ${getDividerClass()} h-full`}
              style={columnBackgroundConfig.style}
            >
              <DropZone zone={`column-${index}`} style={dropZoneStyle} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Columns = {
  fields: {
    columns: {
      type: 'select',
      label: 'Spaltenanzahl',
      options: [
        { label: 'Eine Spalte', value: 1 },
        { label: 'Zwei Spalten', value: 2 },
        { label: 'Drei Spalten', value: 3 },
        { label: 'Vier Spalten', value: 4 }
      ]
    },
    stackOnMobile: {
      type: 'radio',
      label: 'Auf mobilen Geräten stapeln',
      options: [
        { label: 'Ja', value: true },
        { label: 'Nein', value: false }
      ]
    },
    gap: {
      type: 'select',
      label: 'Abstand zwischen Spalten',
      options: [
        { label: 'Sehr klein', value: 'xs' },
        { label: 'Klein', value: 'sm' },
        { label: 'Mittel', value: 'md' },
        { label: 'Groß', value: 'lg' },
        { label: 'Sehr groß', value: 'xl' }
      ]
    },
    verticalAlign: {
      type: 'select',
      label: 'Vertikale Ausrichtung',
      options: [
        { label: 'Oben', value: 'start' },
        { label: 'Zentriert', value: 'center' },
        { label: 'Unten', value: 'end' },
        { label: 'Strecken', value: 'stretch' }
      ]
    },
    backgroundColor: {
      type: 'select',
      label: 'Hintergrundfarbe',
      options: backgroundColorOptions()
    },
    padding: {
      type: 'select',
      label: 'Außenabstand',
      options: [
        { label: 'Kein', value: 'none' },
        { label: 'Klein', value: 'sm' },
        { label: 'Mittel', value: 'md' },
        { label: 'Groß', value: 'lg' },
        { label: 'Sehr groß', value: 'xl' }
      ]
    },
    maxWidth: {
      type: 'select',
      label: 'Maximale Breite',
      options: [
        { label: '3XL', value: '3xl' },
        { label: '4XL', value: '4xl' },
        { label: '5XL', value: '5xl' },
        { label: '6XL', value: '6xl' },
        { label: '7XL', value: '7xl' },
        { label: 'Volle Breite', value: 'full' }
      ]
    },
    columnBackground: {
      type: 'select',
      label: 'Spalten-Hintergrund',
      options: backgroundColorOptions([
        'transparent',
        'white',
        'gray-50',
        'gray-100',
        'motion-plum',
        'motion-midnight'
      ])
    },
    columnPadding: {
      type: 'select',
      label: 'Innenabstand Spalten',
      options: [
        { label: 'Kein', value: 'none' },
        { label: 'Klein', value: 'sm' },
        { label: 'Mittel', value: 'md' },
        { label: 'Groß', value: 'lg' }
      ]
    },
    columnBorderRadius: {
      type: 'select',
      label: 'Abrundung',
      options: [
        { label: 'Keine', value: 'none' },
        { label: 'Klein', value: 'sm' },
        { label: 'Mittel', value: 'md' },
        { label: 'Groß', value: 'lg' },
        { label: 'Sehr groß', value: 'xl' }
      ]
    },
    showDividers: {
      type: 'radio',
      label: 'Rahmen um Spalten',
      options: [
        { label: 'Ja', value: true },
        { label: 'Nein', value: false }
      ]
    },
    dividerColor: {
      type: 'select',
      label: 'Rahmenfarbe',
      options: [
        { label: 'Grau 200', value: 'gray-200' },
        { label: 'Grau 300', value: 'gray-300' },
        { label: 'Blau 200', value: 'blue-200' },
        { label: 'Slate 700', value: 'slate-700' }
      ],
      condition: (values) => values.showDividers === true
    }
  },
  defaultProps: {
    columns: 2,
    stackOnMobile: true,
    gap: 'lg',
    verticalAlign: 'start',
    backgroundColor: 'transparent',
    padding: 'xl',
    maxWidth: '7xl',
    columnBackground: 'transparent',
    columnPadding: 'md',
    columnBorderRadius: 'lg',
    showDividers: false,
    dividerColor: 'gray-200'
  },
  render: ColumnsComponent
};
